import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import ContentContainer from "../../../templates/content-container/contentContainer";
import { useSelector } from "react-redux";
import buildingIcon from "../../../images/building.svg";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import "./summary.scss";
import { useHistory } from "react-router-dom";
import { userSelector } from "../../../slices/user/userSlice";
import Table from "../../../ui-components/table/table";
import { getOutboundSummaryColumns } from "./getOutboundSummaryColumns";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import { getETA } from "./getETA";
import { usePickupSummary } from "./hooks/usePickupSummary";
import { useInboundFreightSummary } from "../../freights/hooks/useInboundFreightSummary";
import { useInhouseSummary } from "./hooks/useInhouseSummary";
import { useInhouseFreightByETA } from "features/freights/hooks/useInhouseFreightByETA";
import { formatToMMDD } from "features/freights/utils/utils";
import moment from "moment";
import { AssignedSummary } from "app/data/freights/model";
import { pluralize } from "../utils";

const REDIRECT_MAP: Record<string, string> = {
  unassigned: "UNASSIGNED_PICKUP",
  assigned: "ASSIGNED_PICKUP",
  completed: "COMPLETED_PICKUP",
  inhouse: "IN_HOUSE_FREIGHT",
  inbound: "INBOUND_FREIGHT",
};

function mergeInboundFreightsWithPickups(
  inboundFreights: any[] = [],
  summaryPickups: any[] = [],
  inhouseQueries: any[] = []
) {
  /** -------------------------------
   * Build inbound map
   -------------------------------- */
  const inboundMap = inboundFreights.reduce((acc: any, freight: any) => {
    acc[freight.originTerminalNumber] = freight;
    return acc;
  }, {});

  /** -------------------------------
   * Build inhouse map
   -------------------------------- */
  const inhouseMap = inhouseQueries.reduce((acc: any, query: any) => {
    if (query?.data?.length) {
      query.data.forEach((item: any) => {
        acc[item.destinationTerminal] = item;
      });
    }
    return acc;
  }, {});

  const usedTerminals = new Set<number>();

  /** -------------------------------
   * Merge into pickup summaries
   -------------------------------- */
  const merged = summaryPickups.map((pickup: any) => {
    const terminalNumber = pickup.serviceCenter?.terminalNumber;

    if (terminalNumber) {
      usedTerminals.add(terminalNumber);
    }

    return {
      ...pickup,
      inboundFreights: inboundMap[terminalNumber] || null,
      inhouseFreights: inhouseMap[terminalNumber] || null,
    };
  });

  /** -------------------------------
   * Add inbound-only terminals
   -------------------------------- */
  const inboundOnly = inboundFreights
    .filter(
      (freight: any) =>
        !usedTerminals.has(freight.originTerminalNumber)
    )
    .map((freight: any) => {
      usedTerminals.add(freight.originTerminalNumber);

      return {
        serviceCenter: {
          terminalNumber: freight.originTerminalNumber,
          terminalName: freight.originTerminalName,
          airportCode: freight.originAirportCode,
        },
        assignedSummary: null,
        unassignedSummary: null,
        completedSummary: null,
        inboundFreights: freight,
        inhouseFreights:
          inhouseMap[freight.originTerminalNumber] || null,
      };
    });

  /** -------------------------------
   * Add inhouse-only terminals
   -------------------------------- */
  const inhouseOnly = Object.values(inhouseMap)
    .filter(
      (inhouse: any) =>
        !usedTerminals.has(inhouse.destinationTerminal)
    )
    .map((inhouse: any) => ({
      serviceCenter: {
        terminalNumber: inhouse.destinationTerminal,
        terminalName: inhouse.destinationName,
        airportCode: inhouse.originAirportCode,
      },
      assignedSummary: null,
      unassignedSummary: null,
      completedSummary: null,
      inboundFreights: null,
      inhouseFreights: inhouse,
    }));

  // sort the result by terminal name
  const sortedResult = [...merged, ...inboundOnly, ...inhouseOnly].sort(
    (a, b) => {
      const terminalA = a.serviceCenter?.terminalName || "";
      const terminalB = b.serviceCenter?.terminalName || "";
      return terminalA.localeCompare(terminalB);
    }
  );

  return sortedResult;
}

export const InboundPickupSummary: React.FC<{}> = () => {
  const userState = useSelector(userSelector);
  const history = useHistory();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [groupBy, setGroupBy] = useState<string>("");
  const [originCity, setOriginCity] = useState<string>("");
  const [typeOfFreight, setTypeOfFreight] = useState<string>("");
  const [destinationTerminal, setDestinationTerminal] = useState<string>("");

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "outbound_summary" });
  }, [userState.activeTerminal?.id]);

  const { data: summaryPickups, isFetching: isFetchingSummaryPickups, error, isLoading } = usePickupSummary()

  const terminalAvailable = !!userState.activeTerminal?.id;

  const { data: inboundFreights, isFetching: isFetchingInboundFreights } = useInboundFreightSummary()

  const { data: inboundFreightsWithOrigin, isLoading: isLoadingInboundFreightsWithOrigin } = useInboundFreightSummary(groupBy, originCity)

  const uniqueTerminals = React.useMemo(() => {
    const terminalsFromInbound =
      inboundFreights?.map((f: any) => f.originTerminalNumber) || [];

    const terminalsFromSummary =
      summaryPickups?.map(
        (p: any) => p.serviceCenter?.terminalNumber
      ) || [];

    const uniqueDestinationTerminals = Array.from(
      new Set([...terminalsFromInbound, ...terminalsFromSummary])
    );

    return uniqueDestinationTerminals.map((destinationTerminal) => ({
      originTerminal: userState.activeTerminal?.id,
      destinationTerminal,
    }));
  }, [inboundFreights, summaryPickups, userState.activeTerminal?.id]);

  const { inhouseQueries: inhouseFreights, isFetchingInhouseSummary } = useInhouseSummary(uniqueTerminals)

  const { data: inhouseFreightsWithOrigin, isLoading: isLoadingInhouseFreightsWithOrigin } = useInhouseFreightByETA('eta', destinationTerminal)

  const mergedData = React.useMemo(() => {
    return mergeInboundFreightsWithPickups(
      inboundFreights,
      summaryPickups,
      inhouseFreights // result of useQueries
    );
  }, [inboundFreights, summaryPickups, inhouseFreights]);

  const handleViewOutboundList = (terminalNumber: any) => {
    history.push(`/pickup/outbound/list?d=${terminalNumber}&s[]=UNASSIGNED_PICKUP&s[]=ASSIGNED_PICKUP&s[]=COMPLETED_PICKUP&s[]=IN_HOUSE_FREIGHT&s[]=INBOUND_FREIGHT`);
  };

  const handleRedirect = (id: string, terminalNumber?: string, etaDate?: string) => {
    const status = REDIRECT_MAP[id];

    if (!status) return;

    if (terminalNumber) {
      history.push(`/pickup/outbound/list?d=${terminalNumber}&s[]=${status}`);
    } else if (destinationTerminal && etaDate) {
      history.push(`/pickup/outbound/list?d=${destinationTerminal}&s[]=${status}&e=${etaDate}`);
    }
    else {
      history.push(`/pickup/outbound/list?s[]=${status}`);
    }
  };

  const onOpenSidebar = (data: any, typeOfFreight: string) => {
    setIsOpen(true);
    setGroupBy("eta");
    setOriginCity(data.terminalDetails.terminalName.toUpperCase());
    setDestinationTerminal(data.terminalDetails.terminalNumber)
    setTypeOfFreight(typeOfFreight)
  }

  const onClose = () => {
    setIsOpen(false);
  }

  const getETAData = (type: string) => {
    if (type === "inbound" && Array.isArray(inboundFreightsWithOrigin)) {
      return inboundFreightsWithOrigin;
    }
    if (type === "inhouse" && Array.isArray(inhouseFreightsWithOrigin)) {
      return inhouseFreightsWithOrigin;
    }
    return [];
  };

  const getTotalShipments = (data: AssignedSummary) => {
    const total = (data.assignedSummary?.totalPickups || 0) +
      (data.unassignedSummary?.totalPickups || 0) +
      (data.completedSummary?.totalPickups || 0) +
      (data.inhouseFreights?.pros || 0) +
      (data.inboundFreights?.numberOfShipments || 0);
    return total.toLocaleString();
  }


  const getTotalPieces = (data: AssignedSummary) => {
    const total = (data.assignedSummary?.totalItems || 0) +
      (data.unassignedSummary?.totalItems || 0) +
      (data.completedSummary?.totalItems || 0) +
      (data.inhouseFreights?.pieces || 0) +
      (data.inboundFreights?.numberOfPieces || 0);
    return total.toLocaleString();
  }

  const getTotalWeight = (data: AssignedSummary) => {
    const total = (data.assignedSummary?.totalWeight || 0) +
      (data.unassignedSummary?.totalWeight || 0) +
      (data.completedSummary?.totalWeight || 0) +
      (data.inhouseFreights?.weight || 0) +
      (data.inboundFreights?.weight || 0);
    return total.toLocaleString();
  }

  const getTotalDrivers = (data: AssignedSummary) => {
    const drivers =
      (data.assignedSummary?.totalDrivers || 0) +
      (data.unassignedSummary?.totalDrivers || 0) +
      (data.completedSummary?.totalDrivers || 0);

    const trailers =
      (data.inhouseFreights?.numberOfTrailers || 0) +
      (data.inboundFreights?.numberOfTrailers || 0);

    return `${trailers} ${pluralize(trailers, "Trailer", "Trailers")}, ` +
      `${drivers} ${pluralize(drivers, "Driver", "Drivers")}`;
  };


  const getETAWindow = (data: AssignedSummary) => {
    const allDates = [
      data.inhouseFreights?.earliestEta,
      data.inhouseFreights?.latestEta,
      data.inboundFreights?.earliestEta,
      data.inboundFreights?.latestEta,
    ].filter(Boolean);

    if (!allDates.length) return "-";

    const moments = allDates.map(d => moment(d, "YYYY-MM-DD"));

    const earliest = moment.min(moments);
    const latest = moment.max(moments);

    return `${earliest.format("MM/DD")} - ${latest.format("MM/DD")}`;
  };

  return (
    <ContentContainer
      isLoading={isFetchingSummaryPickups || isFetchingInboundFreights || isFetchingInhouseSummary}
      title="Outbound Planning Summary"
      tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
      status={"PREVIEW"}
    >
      {(!terminalAvailable) && (
        <XGSErrorMessage>
          <div>
            You do not have assigned terminals.
            Email <a className="white-link" href="mailto:helpdesk@xgsi.com">helpdesk@xgsi.com</a> to request terminal assignment for your account.
          </div>
        </XGSErrorMessage>
      )}
      {(error && terminalAvailable) && <XGSErrorMessage>{error}</XGSErrorMessage>}
      {(mergedData && terminalAvailable) && (
        <div className="xgs-pu-summary__tiles-container">
          {!mergedData.length && !isLoading && <div className="xgs-pu-summary__empty">No pickups, inbound freight, or in-house freight found for the selected terminal.</div>}
          {
            mergedData.map((data, idx) => {
              return (
                <div key={idx} className="xgs-pu-summary__tile">
                  <div className="xgs-pu-summary__tile__header">
                    <div className="xgs-pu-summary__tile__icon">
                      <img src={buildingIcon} alt="" />
                    </div>
                    <span>{data.serviceCenter.airportCode} - {data.serviceCenter.terminalName.toUpperCase()} ({data.serviceCenter.terminalNumber})</span>
                    <div className="xgs-pu-summary__tile__header__button">
                      <Button theme={ButtonThemes.blue} onClick={() => handleViewOutboundList(data.serviceCenter.terminalNumber)}>
                        View Details
                      </Button>
                    </div>
                  </div>
                  <div className="xgs-pu-summary__tile__body xgs-pu-summary__tile__body">
                    <Table
                      columns={getOutboundSummaryColumns(onOpenSidebar, handleRedirect)}
                      rowHeight={32}
                      responsive
                      data={
                        [
                          {
                            type: "No. of Shipments",
                            assigned: data.assignedSummary?.totalPickups?.toLocaleString() ?? '-',
                            unassigned: data.unassignedSummary?.totalPickups?.toLocaleString() ?? '-',
                            completed: data.completedSummary?.totalPickups?.toLocaleString() ?? '-',
                            inhouse: data.inhouseFreights?.pros?.toLocaleString() ?? '-',
                            inbound: data.inboundFreights?.numberOfShipments?.toLocaleString() ?? '-',
                            total: getTotalShipments(data),
                            terminalDetails: data.serviceCenter,
                            isClickable: true,
                          },
                          {
                            type: "No. of Pieces",
                            assigned: data.assignedSummary?.totalItems?.toLocaleString() ?? '-',
                            unassigned: data.unassignedSummary?.totalItems?.toLocaleString() ?? '-',
                            completed: data.completedSummary?.totalItems?.toLocaleString() ?? '-',
                            inhouse: data.inhouseFreights?.pieces?.toLocaleString() ?? '-',
                            inbound: data.inboundFreights?.numberOfPieces?.toLocaleString() ?? '-',
                            total: getTotalPieces(data),
                            terminalDetails: data.serviceCenter
                          },
                          {
                            type: "Weight (lbs)",
                            assigned: data.assignedSummary?.totalWeight?.toLocaleString() ?? '-',
                            unassigned: data.unassignedSummary?.totalWeight?.toLocaleString() ?? '-',
                            completed: data.completedSummary?.totalWeight?.toLocaleString() ?? '-',
                            inhouse: data.inhouseFreights?.weight?.toLocaleString() ?? '-',
                            inbound: data.inboundFreights?.weight?.toLocaleString() ?? '-',
                            total: getTotalWeight(data),
                            terminalDetails: data.serviceCenter
                          },
                          {
                            type: "Trailers/Drivers",
                            assigned: data.assignedSummary?.totalDrivers ?? '-',
                            unassigned: data.unassignedSummary?.totalDrivers ?? '-',
                            completed: data.completedSummary?.totalDrivers ?? '-',
                            inhouse: data.inhouseFreights?.numberOfTrailers ?? '-',
                            inbound: data.inboundFreights?.numberOfTrailers ?? '-',
                            total: getTotalDrivers(data),
                            terminalDetails: data.serviceCenter
                          },
                          {
                            type: "ETA Window",
                            assigned: '-',
                            unassigned: '-',
                            completed: '-',
                            inhouse: (data.inhouseFreights?.earliestEta && data.inhouseFreights?.latestEta) ?
                              `${formatToMMDD(data.inhouseFreights?.earliestEta)} - ${formatToMMDD(data.inhouseFreights?.latestEta)}` : '-',
                            inbound: (data.inboundFreights?.earliestEta && data.inboundFreights?.latestEta) ?
                              `${formatToMMDD(data.inboundFreights?.earliestEta)} - ${formatToMMDD(data.inboundFreights?.latestEta)}` : '-',
                            total: getETAWindow(data),
                            terminalDetails: data.serviceCenter,
                            isClickable: true,
                          },
                        ]
                      }
                    />
                  </div>
                </div>
              )
            })
          }
        </div>
      )}
      <SlideOutSidebar
        header="ETA Window Details"
        onClose={onClose}
        show={isOpen}
        size="small"
      >
        <Table
          columns={getETA(handleRedirect, typeOfFreight)}
          data={getETAData(typeOfFreight)}
          rowHeight={64}
          minTableHeight={420}
          infiniteScroll
          isLoading={isLoadingInboundFreightsWithOrigin || isLoadingInhouseFreightsWithOrigin}
          noResultsText="There are no ETA Window Details"
        />
      </SlideOutSidebar>
    </ContentContainer>
  )
}